import { FC } from 'react'
import { CreditCardOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { SxProps, Theme } from '@mui/material'
import queryString from 'query-string'
import env from 'consts/env'

import { CONTRACT_DOCUMENT_TYPE, ContractDocumentTypes } from 'services/contracts/contracts.consts'
import { useActiveLeaseContractId } from 'services/contracts/hooks/useActiveLeaseContractId'
import { useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'
import { useLeaseContractsPayment } from 'services/contracts/hooks/useLeaseContractsPayment'
import { useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'
import { getApiErrorMessage } from 'utils'

interface AdditionalServicesOnlinePaymentButtonProps {
  documentType: ContractDocumentTypes
  documentId: number
  variant?: 'contained' | 'outlined' | 'text'
  fullWidth?: boolean
  label?: string
  sx?: SxProps<Theme>
}

const AdditionalServicesOnlinePaymentButton: FC<AdditionalServicesOnlinePaymentButtonProps> = ({
  documentId,
  documentType,
  variant,
  fullWidth,
  label,
  sx
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const { formatMessage } = useIntl()
  const { activeLeaseContractId } = useActiveLeaseContractId()
  const { getActiveContract } = useLeaseContractsList()
  const isOperationsFrozen = getActiveContract(activeLeaseContractId)?.isOperationsFrozen
  const {
    getDebitNotesPaymentDetails,
    getInterestNotesPaymentDetails,
    getInvoicesPaymentDetails,
    getDepositPaymentDetails,
    isLoading
  } = useLeaseContractsPayment({
    documentId,
    leaseContractId: activeLeaseContractId
  })

  const openPaymentWindow = ({
    paymentUrl,
    serviceId,
    amount,
    hashHex,
    id,
    title,
    receiverName,
    paymentAccount
  }: {
    amount: string
    hashHex: string
    id: number
    paymentAccount: string
    paymentUrl: string
    serviceId: string
    title: string
    receiverName: string
  }) => {
    const paymentUrlRerdirect = `${paymentUrl}?${queryString.stringify({
      ServiceID: serviceId,
      OrderID: id,
      Amount: amount,
      ReceiverName: receiverName,
      Title: title,
      CustomerNRB: paymentAccount,
      Hash: hashHex
    })}`

    if (!env.IS_PRODUCTION) {
      console.log('paymentUrl:', paymentUrlRerdirect, {
        ServiceID: serviceId,
        OrderID: id,
        Amount: amount,
        ReceiverName: receiverName,
        Title: title,
        CustomerNRB: paymentAccount,
        Hash: hashHex
      })
    }

    return (window.location.href = paymentUrlRerdirect)
  }

  const handleGetPaymentDetails = async () => {
    try {
      if (documentType === CONTRACT_DOCUMENT_TYPE.invoice) {
        const data = await getInvoicesPaymentDetails()
        if (!!data.length) {
          return openPaymentWindow(data[0])
        }
      }
      if (documentType === CONTRACT_DOCUMENT_TYPE.debitNote) {
        const data = await getDebitNotesPaymentDetails()
        if (!!data.length) {
          return openPaymentWindow(data[0])
        }
      }
      if (documentType === CONTRACT_DOCUMENT_TYPE.interestNote) {
        const data = await getInterestNotesPaymentDetails()
        if (!!data.length) {
          return openPaymentWindow(data[0])
        }
      }

      if (documentType === CONTRACT_DOCUMENT_TYPE.deposit) {
        const data = await getDepositPaymentDetails()
        if (!!data.length) {
          return openPaymentWindow(data[0])
        }
      }
    } catch (error: any) {
      const message = getApiErrorMessage(error)
      enqueueSnackbar(
        message ||
          formatMessage({
            id: 'additionalServicesOnlinePaymentButton.error',
            defaultMessage: 'Wystąpił błąd podczas pobierania szczegółów płatności.'
          }),
        { variant: 'error' }
      )
    }
  }

  if (isOperationsFrozen) {
    return null
  }

  return (
    <LoadingButton
      variant={variant || 'text'}
      onClick={handleGetPaymentDetails}
      loading={isLoading}
      fullWidth={fullWidth}
      startIcon={<CreditCardOutlined />}
      sx={sx}>
      {label ||
        formatMessage({
          id: 'additionalServicesOnlinePaymentButton.pay',
          defaultMessage: 'Opłać'
        })}
    </LoadingButton>
  )
}

export default AdditionalServicesOnlinePaymentButton
